import { Link } from "react-router-dom";
import { ArrowRight, Newspaper, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import womanReadingNewspaperImage from "@/assets/woman-reading-newspaper-gossip.jpg";

// Personality: Magazine cover — oversized serif, editorial grid, "Issue No.", pull quote.
const GossLanding = () => {
  return (
    <div className="min-h-screen bg-[hsl(30,25%,97%)]">
      <div className="container mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </div>

      {/* Masthead */}
      <header className="container mx-auto px-6 pb-8 border-b border-foreground/20">
        <div className="flex items-baseline justify-between text-xs uppercase tracking-[0.3em] text-foreground/60">
          <span>Issue No. 01</span>
          <span>Essential Reading</span>
          <span className="hidden sm:inline">Her Season</span>
        </div>
        <h1
          className="mt-4 text-6xl sm:text-8xl lg:text-9xl leading-[0.9] tracking-tight text-foreground"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          All the <em className="italic">Goss</em>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-foreground/70" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          The magazine for women navigating perimenopause and beyond. Hot takes, expert columns,
          real stories, and wellness tips — delivered with the honesty this life stage deserves.
        </p>
      </header>

      {/* Editorial grid */}
      <section className="container mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={womanReadingNewspaperImage} alt="Woman reading" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-5 flex flex-col justify-center">
          <span className="text-xs uppercase tracking-[0.3em] text-primary mb-4">The Cover Story</span>
          <h2 className="text-4xl lg:text-5xl leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Menopause, unfiltered. Written like you're already in on it.
          </h2>
          <p className="text-foreground/70 leading-relaxed mb-4">
            No hushed tones. No clinical distance. Just honest, evidence-informed writing from women
            who have lived it — and the experts they trust.
          </p>
          <p className="text-foreground/70 leading-relaxed">
            Every issue collects the pieces worth reading this week: the hot take, the deep dive, the
            wellness tip you'll actually try, and the news that actually matters.
          </p>
        </div>
      </section>

      {/* Pull quote */}
      <section className="container mx-auto px-6 py-16 border-y border-foreground/15">
        <blockquote
          className="max-w-4xl mx-auto text-center text-3xl md:text-5xl leading-tight italic text-foreground"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          "For too long, menopause has been whispered about. This is the column that says it out loud."
        </blockquote>
        <p className="text-center mt-6 text-sm uppercase tracking-[0.3em] text-foreground/60">— The Editors</p>
      </section>

      {/* Sections */}
      <section className="container mx-auto px-6 py-20">
        <div className="grid md:grid-cols-4 gap-10">
          {[
            { tag: "Hot Takes", desc: "Sharp opinions and cultural commentary you won't read anywhere else." },
            { tag: "Expert Columns", desc: "Clinicians, researchers and coaches, cutting through the noise." },
            { tag: "Real Stories", desc: "First-person accounts from the community. Raw, honest, unforgettable." },
            { tag: "Wellness Tips", desc: "Small, practical shifts that actually change how you feel." },
          ].map((s) => (
            <div key={s.tag} className="border-t border-foreground/40 pt-4">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-2">{s.tag}</p>
              <p className="text-foreground/70 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-6 pb-24 text-center">
        <Button asChild size="lg" className="px-10 py-6 text-lg">
          <Link to="/all-the-goss">
            <Newspaper className="mr-2 h-5 w-5" /> Read this month's issue
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </section>

      <Footer />
    </div>
  );
};

export default GossLanding;
